import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import { FaPaperclip, FaSync, FaWhatsapp } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import { t, tf } from '../utils/i18n';
import './WhatsappAdminPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

function normalizePhoneNumber(raw) {
  const value = String(raw || '').trim();
  if (!value) return '';
  const beforeAt = value.includes('@') ? value.split('@')[0] : value;
  const digits = beforeAt.replace(/\D/g, '');
  return digits || beforeAt;
}

function resolveMsgContact(msg) {
  return msg?.contact || msg?.ticket?.contact || null;
}

function conversationKey(msg) {
  const c = resolveMsgContact(msg);
  const num = normalizePhoneNumber(c?.number || msg?.number);
  if (!num) return `ticket:${msg?.ticketId || 'unknown'}`;
  const group = c?.isGroup ? 'g' : 'd';
  return `${group}:${num}`;
}

function formatPhoneDisplay(number) {
  const digits = normalizePhoneNumber(number);
  if (!digits) return '';
  if (digits.length <= 4) return digits;
  return `+${digits}`;
}

function contactDisplayName(contact, number) {
  const phone = formatPhoneDisplay(number || contact?.number);
  const name = String(contact?.name || '').trim();
  if (name && phone && normalizePhoneNumber(name) !== normalizePhoneNumber(phone)) {
    return { title: name, subtitle: phone };
  }
  return { title: phone || name || '', subtitle: phone && name ? phone : '' };
}

function statusVariant(status, runtimeStatus) {
  const s = String(runtimeStatus || status || '').toUpperCase();
  if (s === 'CONNECTED') return 'success';
  if (s === 'QRCODE' || s === 'OPENING') return 'warning';
  if (s === 'DISCONNECTED' || s === 'ERROR') return 'danger';
  return 'secondary';
}

function isLineConnected(line) {
  if (line?.runtimeStatus) {
    return String(line.runtimeStatus).toUpperCase() === 'CONNECTED';
  }
  return String(line?.status || '').toUpperCase() === 'CONNECTED';
}

function mapWhatsappError(raw, language) {
  const code = String(raw || '').trim();
  const keys = {
    ERR_WAPP_CHECK_CONTACT: 'whatsappAdmin.errWappCheckContact',
    ERR_WAPP_INVALID_CONTACT: 'whatsappAdmin.errWappInvalidContact',
    ERR_WAPP_NOT_INITIALIZED: 'whatsappAdmin.errWappNotInitialized',
    ERR_WAPP_DISABLED: 'whatsappAdmin.errWappDisabled',
  };
  const key = keys[code];
  return key ? t(key, language) : (code || t('whatsappAdmin.errorGeneric', language));
}

function linkifyText(text) {
  const parts = String(text || '').split(/(https?:\/\/[^\s]+)/g);
  return parts.map((part, index) => {
    if (/^https?:\/\//i.test(part)) {
      return (
        <a
          key={`link-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="wa-chat-link"
        >
          {part}
        </a>
      );
    }
    return <span key={`text-${index}`}>{part}</span>;
  });
}

function mediaKind(mediaType) {
  const type = String(mediaType || '').toLowerCase();
  if (!type || type === 'chat' || type === 'text') return null;
  if (type.includes('image') || type === 'image' || type === 'sticker') return 'image';
  if (type.includes('audio') || type === 'audio' || type === 'ptt' || type === 'voice') return 'audio';
  if (type.includes('video') || type === 'video') return 'video';
  if (type.includes('pdf') || type === 'document' || type === 'application') return 'document';
  return 'file';
}

function summarizeMessage(msg, language) {
  const kind = mediaKind(msg?.mediaType);
  const hasMedia = Boolean(msg?.mediaFullUrl || msg?.mediaUrl);
  if (hasMedia || kind) {
    if (kind === 'image') return t('whatsappAdmin.previewImage', language);
    if (kind === 'audio') return t('whatsappAdmin.previewAudio', language);
    if (kind === 'video') return t('whatsappAdmin.previewVideo', language);
    if (kind === 'document') return t('whatsappAdmin.previewDocument', language);
    return t('whatsappAdmin.previewFile', language);
  }
  const body = String(msg?.body || '').trim();
  if (/^https?:\/\//i.test(body)) return t('whatsappAdmin.previewLink', language);
  return body;
}

function MessageBubble({ msg, language }) {
  const kind = mediaKind(msg.mediaType);
  const mediaUrl = msg.mediaFullUrl || msg.mediaUrl;
  const body = String(msg.body || '').trim();
  const isFilenameOnly = mediaUrl && body && body === mediaUrl.split('/').pop();

  return (
    <div className={`wa-bubble ${msg.fromMe ? 'out' : 'in'}`}>
      {mediaUrl && kind === 'image' && (
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="wa-media-link">
          <img src={mediaUrl} alt="" className="wa-media-image" loading="lazy" />
        </a>
      )}
      {mediaUrl && kind === 'video' && (
        <video src={mediaUrl} controls className="wa-media-video" preload="metadata">
          <track kind="captions" />
        </video>
      )}
      {mediaUrl && kind === 'audio' && (
        <audio src={mediaUrl} controls className="wa-media-audio" preload="metadata">
          <track kind="captions" />
        </audio>
      )}
      {mediaUrl && (kind === 'document' || kind === 'file') && (
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer" className="wa-media-doc">
          {t('whatsappAdmin.openFile', language)}
        </a>
      )}
      {body && !isFilenameOnly && (
        <div className="wa-bubble-body">{linkifyText(body)}</div>
      )}
      {!mediaUrl && !body && (
        <div className="wa-bubble-body text-muted">{t('whatsappAdmin.emptyMessage', language)}</div>
      )}
      <div className="wa-bubble-time">
        {new Date(msg.createdAt).toLocaleString(language === 'en' ? 'en-US' : 'es-BO')}
      </div>
    </div>
  );
}

export default function WhatsappAdminPanel({ isAdmin = false }) {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [config, setConfig] = useState(null);
  const [lines, setLines] = useState([]);
  const [chatLineId, setChatLineId] = useState('');
  const [messages, setMessages] = useState([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [draft, setDraft] = useState('');
  const [newLineId, setNewLineId] = useState('');
  const [newLineLabel, setNewLineLabel] = useState('');
  const [newLineName, setNewLineName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [qrLine, setQrLine] = useState(null);
  const [activeTab, setActiveTab] = useState('connection');
  const [manualChats, setManualChats] = useState([]);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [newChatNumber, setNewChatNumber] = useState('');
  const [newChatIsGroup, setNewChatIsGroup] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const fileInputRef = useRef(null);
  const connectionGraceRef = useRef({});

  const stabilizeLineStatus = useCallback((items) => {
    const now = Date.now();
    const graceMs = 45000;
    return items.map((line) => {
      const key = line.lineId || String(line.whatsapp_id || line.id);
      if (isLineConnected(line)) {
        connectionGraceRef.current[key] = now;
        return line;
      }
      const lastOk = connectionGraceRef.current[key] || 0;
      const dbConnected = String(line?.status || '').toUpperCase() === 'CONNECTED';
      if (dbConnected && now - lastOk < graceMs) {
        return { ...line, runtimeStatus: 'CONNECTED' };
      }
      return line;
    });
  }, []);

  const apiFetch = useCallback(async (path, options = {}) => {
    const { timeoutMs = 0, ...fetchOptions } = options;
    const controller = timeoutMs > 0 ? new AbortController() : null;
    const timer = controller
      ? setTimeout(() => controller.abort(), timeoutMs)
      : null;
    try {
      const res = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(fetchOptions.headers || {}) },
        signal: controller?.signal,
        ...fetchOptions,
      });
    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { raw: text };
    }
    if (!res.ok) {
      const raw = data?.error || data?.message || text || res.statusText;
      const msg = typeof raw === 'string' ? mapWhatsappError(raw, language) : 'Request failed';
      throw new Error(msg);
    }
    return data;
    } catch (err) {
      if (err?.name === 'AbortError') {
        throw new Error(t('whatsappAdmin.sendTimeout', language));
      }
      throw err;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }, [language]);

  const loadConfig = useCallback(async () => {
    const cfg = await apiFetch('/api/admin/whatsapp/config');
    setConfig(cfg);
    return cfg;
  }, [apiFetch]);

  const loadLines = useCallback(async () => {
    const data = await apiFetch('/api/admin/whatsapp/connections');
    const items = stabilizeLineStatus(data?.items || []);
    setLines(items);
    return items;
  }, [apiFetch, stabilizeLineStatus]);

  const loadInbox = useCallback(async (lineId) => {
    if (!lineId) {
      setMessages([]);
      return [];
    }
    const since = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const data = await apiFetch(
      `/api/admin/whatsapp/inbox?lineId=${encodeURIComponent(lineId)}&since=${encodeURIComponent(since)}&limit=200&direction=all`
    );
    const items = data?.items || [];
    setMessages(items);
    return items;
  }, [apiFetch]);

  const refreshAll = useCallback(async () => {
    setError('');
    try {
      await loadConfig();
      const items = await loadLines();
      if (items.length > 0) {
        setChatLineId((prev) => {
          const connected = items.filter(isLineConnected);
          if (prev && connected.some((l) => l.lineId === prev)) return prev;
          if (connected.length > 0) return connected[0].lineId;
          return prev || items[0].lineId;
        });
      }
    } catch (e) {
      setError(e.message || t('whatsappAdmin.errorGeneric', language));
    } finally {
      setLoading(false);
    }
  }, [loadConfig, loadLines, language]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    if (!chatLineId || !config?.configured) return undefined;
    loadInbox(chatLineId).catch(() => {});
    const pollMs = qrLine?.qrcode ? 3000 : 15000;
    const id = setInterval(() => {
      loadLines().catch(() => {});
      if (activeTab === 'chats') loadInbox(chatLineId).catch(() => {});
    }, pollMs);
    return () => clearInterval(id);
  }, [chatLineId, config?.configured, activeTab, loadLines, loadInbox, qrLine?.qrcode]);

  const qrWhatsappId = qrLine?.whatsapp_id ?? qrLine?.id ?? null;

  const openQrModal = useCallback(async (line) => {
    const wid = line?.whatsapp_id ?? line?.id;
    if (!wid) return;
    setError('');
    try {
      const detail = await apiFetch(`/api/admin/whatsapp/connections/${wid}`);
      if (isLineConnected({ ...line, ...detail })) {
        setSuccess(tf('whatsappAdmin.lineConnected', { label: line.label || line.lineId }, language));
        return;
      }
      setQrLine({ ...line, ...detail, whatsapp_id: wid });
    } catch (err) {
      setError(err.message);
    }
  }, [apiFetch, language]);

  useEffect(() => {
    if (!qrWhatsappId) return undefined;

    const pollQr = async () => {
      try {
        const detail = await apiFetch(`/api/admin/whatsapp/connections/${qrWhatsappId}`);
        if (isLineConnected(detail)) {
          setQrLine(null);
          setSuccess(tf('whatsappAdmin.lineConnected', { label: detail.label || detail.name || qrWhatsappId }, language));
          await loadLines();
          return;
        }
        if (detail?.qrcode) {
          setQrLine((prev) => ({
            ...(prev || {}),
            ...detail,
            whatsapp_id: qrWhatsappId,
            qrcode: detail.qrcode,
          }));
        }
      } catch {
        /* polling */
      }
    };

    pollQr();
    const id = setInterval(pollQr, 3000);
    return () => clearInterval(id);
  }, [qrWhatsappId, apiFetch, loadLines, language]);

  useEffect(() => {
    setSelectedKey('');
    setDraft('');
    if (chatLineId) loadInbox(chatLineId).catch(() => {});
  }, [chatLineId, loadInbox]);

  const chatLine = lines.find((l) => l.lineId === chatLineId) || null;
  const anyConnected = lines.some(isLineConnected);
  const chatLineConnected = chatLine ? isLineConnected(chatLine) : false;

  const conversations = useMemo(() => {
    const map = new Map();
    messages.forEach((msg) => {
      const key = conversationKey(msg);
      const contact = resolveMsgContact(msg);
      const number = normalizePhoneNumber(contact?.number || msg?.number);
      const prev = map.get(key);
      const created = new Date(msg.createdAt || 0).getTime();
      if (!prev || created > prev.lastAt) {
        map.set(key, {
          key,
          contact,
          lastBody: summarizeMessage(msg, language),
          lastAt: created,
          isGroup: !!contact?.isGroup,
          number,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => b.lastAt - a.lastAt);
  }, [messages, language]);

  const allConversations = useMemo(() => {
    const inboxKeys = new Set(conversations.map((c) => c.key));
    const extra = manualChats.filter((c) => !inboxKeys.has(c.key));
    return [...extra, ...conversations].sort((a, b) => b.lastAt - a.lastAt);
  }, [conversations, manualChats]);

  const threadMessages = useMemo(() => {
    if (!selectedKey) return [];
    return messages
      .filter((m) => conversationKey(m) === selectedKey)
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  }, [messages, selectedKey]);

  const selectedConversation = allConversations.find((c) => c.key === selectedKey);

  const resolveSendTarget = () => {
    if (!selectedKey) return null;
    const conv = allConversations.find((c) => c.key === selectedKey);
    if (conv?.number) return conv;
    const match = selectedKey.match(/^(g|d):(.+)$/);
    if (match) {
      return { key: selectedKey, number: match[2], isGroup: match[1] === 'g', contact: null };
    }
    return null;
  };

  const withBusy = async (whatsappId, fn) => {
    setBusyId(whatsappId);
    try {
      await fn();
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateLine = async (e) => {
    e.preventDefault();
    if (!newLineId.trim() || !newLineLabel.trim() || !newLineName.trim()) return;
    setError('');
    setSuccess('');
    try {
      const created = await apiFetch('/api/admin/whatsapp/lines', {
        method: 'POST',
        body: JSON.stringify({
          lineId: newLineId.trim(),
          label: newLineLabel.trim(),
          name: newLineName.trim(),
        }),
      });
      setSuccess(t('whatsappAdmin.lineCreated', language));
      setShowCreateModal(false);
      setNewLineId('');
      setNewLineLabel('');
      setNewLineName('');
      const wid = created?.whatsapp_id || created?.id;
      if (wid) {
        await apiFetch(`/api/admin/whatsapp/session/${wid}/start`, { method: 'POST' });
        const detail = await apiFetch(`/api/admin/whatsapp/connections/${wid}`);
        if (detail?.qrcode) {
          setQrLine(detail);
        }
      }
      await refreshAll();
      if (created?.lineId) setChatLineId(created.lineId);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleStartSession = (line) =>
    withBusy(line.whatsapp_id, async () => {
      setError('');
      const data = await apiFetch(`/api/admin/whatsapp/session/${line.whatsapp_id}/start`, { method: 'POST' });
      if (data?.session?.qrcode) {
        await openQrModal({ ...line, ...data.session });
      } else {
        await openQrModal(line);
      }
      setSuccess(tf('whatsappAdmin.sessionStartedFor', { label: line.label }, language));
      await loadLines();
    }).catch((err) => setError(err.message));

  const handleNewQr = (line) =>
    withBusy(line.whatsapp_id, async () => {
      await apiFetch(`/api/admin/whatsapp/session/${line.whatsapp_id}/new-qr`, { method: 'PUT' });
      const detail = await apiFetch(`/api/admin/whatsapp/connections/${line.whatsapp_id}`);
      if (detail?.qrcode) await openQrModal({ ...line, ...detail });
      await loadLines();
    }).catch((err) => setError(err.message));

  const handleDisconnect = (line) => {
    if (!window.confirm(tf('whatsappAdmin.disconnectConfirmFor', { label: line.label }, language))) return;
    return withBusy(line.whatsapp_id, async () => {
      await apiFetch(`/api/admin/whatsapp/session/${line.whatsapp_id}/disconnect`, { method: 'DELETE' });
      setSuccess(tf('whatsappAdmin.disconnectedFor', { label: line.label }, language));
      await loadLines();
    }).catch((err) => setError(err.message));
  };

  const handleRemoveFromPanel = (line) => {
    if (!window.confirm(tf('whatsappAdmin.removeLineConfirm', { label: line.label }, language))) return;
    return withBusy(line.whatsapp_id, async () => {
      await apiFetch(`/api/admin/whatsapp/lines/${line.lineId}`, { method: 'DELETE' });
      if (chatLineId === line.lineId) setChatLineId('');
      await refreshAll();
    }).catch((err) => setError(err.message));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const target = resolveSendTarget();
    const text = draft.trim();
    if (!chatLineConnected) {
      setError(t('whatsappAdmin.chatLineNotConnected', language));
      return;
    }
    if ((!text && !attachFile) || !target?.number || !chatLineId) {
      if (!target?.number) {
        setError(t('whatsappAdmin.missingPhoneForSend', language));
      } else if (!text && !attachFile) {
        setError(t('whatsappAdmin.messageOrMediaRequired', language));
      }
      return;
    }
    setBusyId(chatLine?.whatsapp_id || 'send');
    setError('');
    setSuccess('');
    try {
      if (attachFile) {
        const form = new FormData();
        form.append('lineId', chatLineId);
        form.append('number', target.number);
        form.append('body', text);
        form.append('isGroup', target.isGroup ? 'true' : 'false');
        form.append('medias', attachFile);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 90000);
        try {
          const res = await fetch(`${API_BASE}/api/admin/whatsapp/messages/send`, {
            method: 'POST',
            credentials: 'include',
            body: form,
            signal: controller.signal,
          });
          const resText = await res.text();
          let data = {};
          try {
            data = resText ? JSON.parse(resText) : {};
          } catch {
            data = { raw: resText };
          }
          if (!res.ok) {
            const raw = data?.error || data?.message || resText || res.statusText;
            throw new Error(typeof raw === 'string' ? mapWhatsappError(raw, language) : t('whatsappAdmin.errorGeneric', language));
          }
        } catch (err) {
          if (err?.name === 'AbortError') {
            throw new Error(t('whatsappAdmin.sendTimeout', language));
          }
          throw err;
        } finally {
          clearTimeout(timer);
        }
      } else {
        await apiFetch('/api/admin/whatsapp/messages/send', {
          method: 'POST',
          timeoutMs: 90000,
          body: JSON.stringify({
            lineId: chatLineId,
            number: target.number,
            body: text,
            isGroup: !!target.isGroup,
          }),
        });
      }
      setDraft('');
      setAttachFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setSuccess(t('whatsappAdmin.messageSent', language));
      await loadInbox(chatLineId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const handleStartNewChat = (e) => {
    e.preventDefault();
    if (!chatLineConnected) {
      setError(t('whatsappAdmin.chatLineNotConnected', language));
      return;
    }
    const digits = normalizePhoneNumber(newChatNumber);
    if (digits.length < 8) {
      setError(t('whatsappAdmin.invalidPhoneNumber', language));
      return;
    }
    setError('');
    const key = `${newChatIsGroup ? 'g' : 'd'}:${digits}`;
    const entry = {
      key,
      number: digits,
      isGroup: newChatIsGroup,
      contact: { name: formatPhoneDisplay(digits), number: digits, isGroup: newChatIsGroup },
      lastBody: '',
      lastAt: Date.now(),
      isManual: true,
    };
    setManualChats((prev) => {
      if (prev.some((c) => c.key === key)) return prev;
      return [entry, ...prev];
    });
    setSelectedKey(key);
    setShowNewChatModal(false);
    setNewChatNumber('');
    setNewChatIsGroup(false);
    setActiveTab('chats');
  };

  const applyPreset = (preset) => {
    if (preset === 'paraguay') {
      setNewLineId('paraguay');
      setNewLineLabel(language === 'en' ? 'Paraguay' : 'Paraguay');
      setNewLineName('EstuMejorOpcion Paraguay');
    } else if (preset === 'bolivia') {
      setNewLineId('bolivia');
      setNewLineLabel(language === 'en' ? 'Bolivia' : 'Bolivia');
      setNewLineName('EstuMejorOpcion Bolivia');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-2 text-muted">{t('whatsappAdmin.loading', language)}</p>
      </div>
    );
  }

  return (
    <div className="whatsapp-admin-panel">
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      {!config?.configured && (
        <Alert variant="warning">{t('whatsappAdmin.notConfiguredServer', language)}</Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'connection')} className="mb-3">
        <Tab eventKey="connection" title={t('whatsappAdmin.tabConnection', language)}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div>
              <h2 className="h5 mb-1">{t('whatsappAdmin.multiLineTitle', language)}</h2>
              <p className="text-muted small mb-0">{t('whatsappAdmin.multiLineHelp', language)}</p>
              <p className="text-muted small mb-0 mt-1">{t('whatsappAdmin.linkOneLineHint', language)}</p>
            </div>
            <div className="d-flex gap-2">
              {isAdmin && (
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)} disabled={!config?.hasToken}>
                  {t('whatsappAdmin.registerNewLine', language)}
                </Button>
              )}
              <Button variant="outline-secondary" size="sm" onClick={refreshAll}>
                <FaSync className="me-1" /> {t('whatsappAdmin.refresh', language)}
              </Button>
            </div>
          </div>

          {lines.length === 0 ? (
            <Card>
              <Card.Body className="text-center py-4">
                <p className="text-muted mb-3">{t('whatsappAdmin.noLinesRegistered', language)}</p>
                {isAdmin && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)} disabled={!config?.hasToken}>
                    {t('whatsappAdmin.registerNewLine', language)}
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : (
            <Row className="g-3">
              {lines.map((line) => {
                const connected = isLineConnected(line);
                const busy = busyId === line.whatsapp_id;
                return (
                  <Col key={line.lineId} md={6} xl={6}>
                    <Card className={`h-100 wa-line-card${connected ? ' wa-line-connected' : ''}`}>
                      <Card.Body>
                        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                          <div>
                            <Badge bg="info" className="me-2">{line.lineId}</Badge>
                            <strong>{line.label}</strong>
                            <div className="small text-muted mt-1">{line.name || line.line_name}</div>
                          </div>
                          <Badge bg={statusVariant(line.status, line.runtimeStatus)}>
                            {line.runtimeStatus || line.status}
                          </Badge>
                        </div>
                        <div className="small text-muted mb-3">
                          {tf('whatsappAdmin.lineIdLabel', { id: line.whatsapp_id }, language)}
                          {line.runtimePhoneNumber && (
                            <> · {tf('whatsappAdmin.phoneLabel', { phone: line.runtimePhoneNumber }, language)}</>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <Button variant="success" size="sm" disabled={busy || connected} onClick={() => handleStartSession(line)}>
                            {t('whatsappAdmin.startSession', language)}
                          </Button>
                          <Button variant="warning" size="sm" disabled={busy} onClick={() => handleNewQr(line)}>
                            {t('whatsappAdmin.newQr', language)}
                          </Button>
                          {line.qrcode && (
                            <Button variant="outline-primary" size="sm" disabled={busy} onClick={() => openQrModal(line)}>
                              {t('whatsappAdmin.showQr', language)}
                            </Button>
                          )}
                          <Button variant="outline-danger" size="sm" disabled={busy} onClick={() => handleDisconnect(line)}>
                            {t('whatsappAdmin.disconnect', language)}
                          </Button>
                          {isAdmin && (
                            <Button variant="outline-secondary" size="sm" disabled={busy} onClick={() => handleRemoveFromPanel(line)}>
                              {t('whatsappAdmin.removeFromPanel', language)}
                            </Button>
                          )}
                        </div>
                        {connected && (
                          <Button
                            variant="link"
                            size="sm"
                            className="px-0 mt-2"
                            onClick={() => {
                              setChatLineId(line.lineId);
                              setActiveTab('chats');
                            }}
                          >
                            {t('whatsappAdmin.openChats', language)}
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Tab>

        <Tab eventKey="chats" title={t('whatsappAdmin.tabChats', language)} disabled={!anyConnected}>
          {lines.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>{t('whatsappAdmin.chatAsLine', language)}</Form.Label>
              <Form.Select
                value={chatLineId}
                onChange={(e) => setChatLineId(e.target.value)}
              >
                {lines.map((line) => (
                  <option key={line.lineId} value={line.lineId} disabled={!isLineConnected(line)}>
                    {line.label} ({line.lineId}) — {line.runtimeStatus || line.status}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          {!chatLineConnected ? (
            <Alert variant="secondary">{t('whatsappAdmin.chatLineNotConnected', language)}</Alert>
          ) : (
            <div className="wa-chat-layout">
              <div className="wa-chat-sidebar">
                <div className="wa-chat-sidebar-header d-flex justify-content-between align-items-center gap-2">
                  <span>{tf('whatsappAdmin.conversationsFor', { label: chatLine?.label || chatLineId }, language)}</span>
                  <Button variant="outline-success" size="sm" onClick={() => setShowNewChatModal(true)}>
                    {t('whatsappAdmin.newConversation', language)}
                  </Button>
                </div>
                <div className="wa-chat-list">
                  {allConversations.length === 0 && (
                    <p className="small text-muted p-3 mb-0">{t('whatsappAdmin.noConversations', language)}</p>
                  )}
                  {allConversations.map((c) => {
                    const display = contactDisplayName(c.contact, c.number);
                    return (
                      <button
                        type="button"
                        key={c.key}
                        className={`wa-chat-list-item${selectedKey === c.key ? ' active' : ''}`}
                        onClick={() => setSelectedKey(c.key)}
                      >
                        <div className="wa-chat-list-name">{display.title || t('whatsappAdmin.unknownContact', language)}</div>
                        {display.subtitle && display.subtitle !== display.title && (
                          <div className="wa-chat-list-phone">{display.subtitle}</div>
                        )}
                        {!display.subtitle && c.number && (
                          <div className="wa-chat-list-phone">{formatPhoneDisplay(c.number)}</div>
                        )}
                        <div className="wa-chat-list-preview text-truncate">{c.lastBody || (c.isManual ? t('whatsappAdmin.newChatHint', language) : '')}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="wa-chat-main">
                {!selectedKey ? (
                  <div className="wa-chat-empty">{t('whatsappAdmin.selectConversation', language)}</div>
                ) : (
                  <>
                    <div className="wa-chat-header">
                      {(() => {
                        const display = contactDisplayName(selectedConversation?.contact, selectedConversation?.number);
                        return (
                          <>
                            <div>{display.title || t('whatsappAdmin.unknownContact', language)}</div>
                            {(display.subtitle || selectedConversation?.number) && (
                              <div className="wa-chat-header-phone">
                                {formatPhoneDisplay(selectedConversation?.number || display.subtitle)}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="wa-chat-messages">
                      {threadMessages.map((m) => (
                        <MessageBubble key={`${m.id}-${m.createdAt}`} msg={m} language={language} />
                      ))}
                    </div>
                    <form className="wa-chat-compose" onSubmit={handleSend}>
                      {attachFile && (
                        <div className="wa-attach-preview">
                          <span className="text-truncate">{attachFile.name}</span>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="p-0 ms-2"
                            onClick={() => {
                              setAttachFile(null);
                              if (fileInputRef.current) fileInputRef.current.value = '';
                            }}
                          >
                            {t('whatsappAdmin.removeAttachment', language)}
                          </Button>
                        </div>
                      )}
                      <div className="wa-chat-compose-row">
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="d-none"
                          accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                          onChange={(ev) => setAttachFile(ev.target.files?.[0] || null)}
                        />
                        <Button
                          type="button"
                          variant="outline-light"
                          title={t('whatsappAdmin.attachFile', language)}
                          disabled={busyId != null}
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <FaPaperclip />
                        </Button>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          placeholder={t('whatsappAdmin.messagePlaceholder', language)}
                          disabled={busyId != null}
                        />
                        <Button
                          type="submit"
                          variant="success"
                          disabled={busyId != null || (!draft.trim() && !attachFile) || !resolveSendTarget()?.number}
                        >
                          {busyId != null ? t('whatsappAdmin.sendingMessage', language) : t('whatsappAdmin.send', language)}
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          )}
        </Tab>
      </Tabs>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('whatsappAdmin.createLineTitle', language)}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateLine}>
          <Modal.Body>
            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button type="button" variant="outline-secondary" size="sm" onClick={() => applyPreset('paraguay')}>
                {t('whatsappAdmin.presetParaguay', language)}
              </Button>
              <Button type="button" variant="outline-secondary" size="sm" onClick={() => applyPreset('bolivia')}>
                {t('whatsappAdmin.presetBolivia', language)}
              </Button>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>{t('whatsappAdmin.lineIdSlugLabel', language)}</Form.Label>
              <Form.Control
                value={newLineId}
                onChange={(e) => setNewLineId(e.target.value)}
                placeholder={t('whatsappAdmin.lineIdSlugPlaceholder', language)}
                required
                minLength={2}
                pattern="[a-z0-9_-]+"
              />
              <Form.Text>{t('whatsappAdmin.lineIdSlugHelp', language)}</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('whatsappAdmin.lineLabelField', language)}</Form.Label>
              <Form.Control
                value={newLineLabel}
                onChange={(e) => setNewLineLabel(e.target.value)}
                placeholder={t('whatsappAdmin.lineLabelPlaceholder', language)}
                required
                minLength={2}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>{t('whatsappAdmin.lineNameLabel', language)}</Form.Label>
              <Form.Control
                value={newLineName}
                onChange={(e) => setNewLineName(e.target.value)}
                placeholder={t('whatsappAdmin.lineNamePlaceholder', language)}
                required
                minLength={2}
              />
              <Form.Text>{t('whatsappAdmin.lineNameWhaticketHelp', language)}</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>{t('whatsappAdmin.cancel', language)}</Button>
            <Button type="submit" variant="primary">{t('whatsappAdmin.createAndStart', language)}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showNewChatModal} onHide={() => setShowNewChatModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('whatsappAdmin.newConversationTitle', language)}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleStartNewChat}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('whatsappAdmin.newConversationNumber', language)}</Form.Label>
              <Form.Control
                value={newChatNumber}
                onChange={(e) => setNewChatNumber(e.target.value)}
                placeholder={t('whatsappAdmin.newConversationNumberPlaceholder', language)}
                required
                inputMode="tel"
              />
              <Form.Text>{t('whatsappAdmin.newConversationNumberHelp', language)}</Form.Text>
            </Form.Group>
            <Form.Check
              type="checkbox"
              id="wa-new-chat-group"
              label={t('whatsappAdmin.newConversationIsGroup', language)}
              checked={newChatIsGroup}
              onChange={(e) => setNewChatIsGroup(e.target.checked)}
            />
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowNewChatModal(false)}>{t('whatsappAdmin.cancel', language)}</Button>
            <Button type="submit" variant="success">{t('whatsappAdmin.newConversationStart', language)}</Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(qrLine?.qrcode)} onHide={() => setQrLine(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {tf('whatsappAdmin.scanQrFor', { label: qrLine?.label || qrLine?.lineId || '' }, language)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p className="text-muted">{t('whatsappAdmin.scanQrHelp', language)}</p>
          <p className="small text-warning">{t('whatsappAdmin.qrExpiresHint', language)}</p>
          {qrLine?.qrcode && (
            <div className="d-inline-block p-3 bg-white rounded">
              <QRCodeSVG value={qrLine.qrcode} size={256} level="M" />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
